import React, { useState, useEffect, useCallback } from 'react';
import { useChatroom } from '../../contexts/ChatroomContext';
import './CreateChannelModal.css';

export function CreateChannelModal({ onClose, onChannelCreated }) {
  const { personas, loadPersonas, createChannel } = useChatroom();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerOnLlmMessage, setTriggerOnLlmMessage] = useState(false);
  const [bidCycleEnabled, setBidCycleEnabled] = useState(true);
  const [selectedPersonaIds, setSelectedPersonaIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPersonas, setIsLoadingPersonas] = useState(false);
  const [error, setError] = useState(null);

  // Load personas on mount
  useEffect(() => {
    const fetchPersonas = async () => {
      if (personas.length === 0) {
        setIsLoadingPersonas(true);
        try {
          await loadPersonas();
        } catch (err) {
          console.error('Failed to load personas:', err);
        } finally {
          setIsLoadingPersonas(false);
        }
      }
    };
    fetchPersonas();
  }, [personas.length, loadPersonas]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handlePersonaToggle = useCallback((personaId) => {
    setSelectedPersonaIds(prev => {
      if (prev.includes(personaId)) {
        return prev.filter(id => id !== personaId);
      } else {
        return [...prev, personaId];
      }
    });
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Channel name is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const settings = {
        trigger_on_llm_message: triggerOnLlmMessage,
        bid_cycle_enabled: bidCycleEnabled
      };

      const channel = await createChannel(name.trim(), description.trim(), settings, selectedPersonaIds);

      if (onChannelCreated) {
        onChannelCreated(channel);
      }
      onClose();
    } catch (err) {
      console.error('Failed to create channel:', err);
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.response?.data?.name) {
        setError(err.response.data.name[0]);
      } else {
        setError('Failed to create channel. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [name, description, triggerOnLlmMessage, bidCycleEnabled, selectedPersonaIds, createChannel, onChannelCreated, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-backdrop create-channel-modal" onClick={handleBackdropClick}>
      <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="create-channel-title">
        <div className="modal-header">
          <h2 id="create-channel-title">Create New Channel</h2>
          <button className="close-button" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        {error && (
          <div className="modal-error">
            {error}
            <button onClick={() => setError(null)} aria-label="Dismiss error">&times;</button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Channel Name */}
            <div className="form-group">
              <label htmlFor="channel-name">Channel Name *</label>
              <input
                id="channel-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., general, philosophy, coding-help"
                className="form-input"
                autoFocus
                disabled={isLoading}
              />
            </div>

            {/* Description */}
            <div className="form-group">
              <label htmlFor="channel-description">Description</label>
              <textarea
                id="channel-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What is this channel about?"
                className="form-textarea"
                rows={3}
                disabled={isLoading}
              />
            </div>

            {/* Settings Section */}
            <section className="settings-section">
              <h3>Channel Settings</h3>

              <div className="setting-row">
                <label className="setting-label">
                  <input
                    type="checkbox"
                    checked={bidCycleEnabled}
                    onChange={(e) => setBidCycleEnabled(e.target.checked)}
                    disabled={isLoading}
                  />
                  <span className="setting-text">
                    <strong>Enable Bid Cycles</strong>
                    <span className="setting-description">
                      AI participants bid on messages to determine who responds. Disable for direct @ mentions only.
                    </span>
                  </span>
                </label>
              </div>

              <div className="setting-row">
                <label className="setting-label">
                  <input
                    type="checkbox"
                    checked={triggerOnLlmMessage}
                    onChange={(e) => setTriggerOnLlmMessage(e.target.checked)}
                    disabled={isLoading}
                  />
                  <span className="setting-text">
                    <strong>LLM-to-LLM Conversations</strong>
                    <span className="setting-description">
                      When enabled, AI responses trigger new bid cycles, creating continuous AI conversations.
                    </span>
                  </span>
                </label>
              </div>
            </section>

            {/* Persona Selection */}
            <section className="personas-section">
              <h3>Add AI Participants</h3>
              <p className="section-description">
                Select AI personas to add to this channel. You can add more later.
              </p>

              {isLoadingPersonas ? (
                <div className="loading">Loading personas...</div>
              ) : personas.length === 0 ? (
                <div className="no-personas">No AI personas available</div>
              ) : (
                <div className="persona-list">
                  {personas.map(persona => (
                    <label key={persona.id} className="persona-item">
                      <input
                        type="checkbox"
                        checked={selectedPersonaIds.includes(persona.id)}
                        onChange={() => handlePersonaToggle(persona.id)}
                        disabled={isLoading}
                      />
                      <div className="persona-info">
                        <span className="persona-name">{persona.name}</span>
                        {persona.model_config && (
                          <span className="persona-model">
                            {persona.model_config.display_name || persona.model_config.identifier}
                          </span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </section>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="cancel-button"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="create-button"
              disabled={isLoading || !name.trim()}
            >
              {isLoading ? 'Creating...' : 'Create Channel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
